import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";

import RoleIdentityContext from "src/contexts/data/RoleIdentityContext";
import useRoleIdentityById from "src/hooks/data/load/useRoleIdentityById";

/**
 * Loads role identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RouteRoleIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { roleId } = useParams<{ roleId: string }>();
    const context = useRoleIdentityById(roleId ? Number.parseInt(roleId, 10) : undefined);

    return <RoleIdentityContext.Provider value={context}>{children}</RoleIdentityContext.Provider>;
};
